import React, { useState } from "react";
import { X, Upload, FileText } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TICKET_CATEGORIES, PriorityBangs } from "./TicketCard";

export default function TicketModal({ mode = "create", initial = null, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      title: "",
      description: "",
      page_name: "",
      priority: 3,
      category: "new_page",
    }
  );
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      let ticket;
      if (mode === "create") {
        const { data } = await api.post("/tickets", form);
        ticket = data;
        if (file) {
          setUploading(true);
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/tickets/${ticket.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Ticket created");
      } else {
        const { data } = await api.put(`/tickets/${initial.id}`, form);
        ticket = data;
        if (file) {
          setUploading(true);
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/tickets/${ticket.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Ticket updated");
      }
      onSaved(ticket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="ticket-modal" className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{mode === "create" ? "New" : "Edit"}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">Work Ticket</h2>
          </div>
          <button data-testid="ticket-modal-close" onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <Field label="Title" required>
            <input data-testid="ticket-form-title" autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Description">
            <textarea data-testid="ticket-form-description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Page Name">
              <input data-testid="ticket-form-page" value={form.page_name} onChange={(e) => set("page_name", e.target.value)} placeholder="e.g. /about" className={inputCls} />
            </Field>
            <Field label="Category">
              <select data-testid="ticket-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {Object.entries(TICKET_CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Priority">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`ticket-form-priority-${n}`}
                  onClick={() => set("priority", n)}
                  className={`flex h-10 flex-1 items-center justify-center rounded-md border transition-colors ${
                    form.priority === n ? "border-[#0066ff] bg-[#E5F0FF]" : "border-zinc-200 bg-white hover:bg-zinc-50"
                  }`}
                >
                  <PriorityBangs level={n} />
                </button>
              ))}
            </div>
          </Field>
          <Field label="Attach Document">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-3 text-sm text-zinc-600 hover:border-[#0066ff] hover:text-[#0066ff]">
              <Upload size={16} />
              <input
                data-testid="ticket-form-file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <span>{file ? file.name : "Choose a file (optional)"}</span>
            </label>
            {initial?.attachment_name && !file && (
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                <FileText size={12} /> Current: {initial.attachment_name}
              </div>
            )}
          </Field>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="ticket-modal-save" onClick={save} disabled={saving || uploading} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98] disabled:opacity-50">
            {uploading ? "Uploading…" : saving ? "Saving…" : (mode === "create" ? "Create Ticket" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium text-zinc-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}
